import {
  MESES_DEL_ANO,
  MESES_GRATIS_AL_ANO,
  MESES_QUE_SE_PAGAN,
  PLANS,
  listedPlans,
  yearlyMonthlyEquivalentUsdCents,
  formatUsd,
  type Plan,
} from '@qr/shared/plans';
// El texto de cada plan vive en un solo sitio: el panel ensena exactamente
// estas mismas lineas en la pantalla de contratar.
import { comoSeCobra, rasgosDe } from '@qr/shared/plan-copy';
import NumberFlow, { usePrefersReducedMotion } from '@number-flow/react';
import { Check, Minus, Star } from 'lucide-react';
import { useId, useRef, useState } from 'react';

/**
 * Los planes: la única parte de la página que se lee con calma.
 *
 * Todo lo demás se hojea; aquí alguien compara tres columnas y decide. Por eso
 * es la sección con más sitio, la única con un número grande y la única que se
 * permite una animación —el precio que rueda al cambiar de ciclo—, porque ahí
 * la animación *informa*: enseña que ese número acaba de cambiar y cuánto.
 */

const ADMIN_URL = import.meta.env.VITE_ADMIN_URL ?? 'http://localhost:5173';

/*
 * Dolares, con los dos decimales SIEMPRE.
 *
 * Con `minimumFractionDigits: 0`, el equivalente mensual del plan anual sale
 * "$7.5", que no es como se escribe el dinero y parece un error de programa.
 * Mejor "$9.00" junto a "$7.50" que una cifra a medias.
 *
 * Para la prosa —"$90 al año"— se usa `formatUsd`, que si quita los decimales
 * cuando sobran: ahi la cifra se lee dentro de una frase y no compite con
 * ninguna otra.
 */
const DOLARES = {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
} as const;

type Ciclo = 'mes' | 'ano';

/** En dolares con decimales, que es lo que pinta el contador animado. */
function precioDe(plan: Plan, ciclo: Ciclo): number {
  const mensual = plan.monthlyPriceUsdCents ?? 0;
  const centavos = ciclo === 'mes' ? mensual : (yearlyMonthlyEquivalentUsdCents(plan) ?? mensual);
  return centavos / 100;
}

export function Precios() {
  const [ciclo, setCiclo] = useState<Ciclo>('mes');
  const mando = useRef<HTMLDivElement>(null);
  const sinMovimiento = usePrefersReducedMotion();
  const etiqueta = useId();

  /**
   * Celebrar el cambio a anual con confeti.
   *
   * La librería se carga solo cuando alguien pulsa, no al abrir la página: son
   * unos 6 kB que la inmensa mayoría de las visitas no llega a necesitar. Y no
   * se lanza si el sistema pide menos movimiento, que para algunas personas no
   * es una preferencia estética sino un mareo.
   */
  async function celebrar() {
    if (sinMovimiento || !mando.current) return;
    const caja = mando.current.getBoundingClientRect();
    const { default: confetti } = await import('canvas-confetti');
    confetti({
      particleCount: 60,
      spread: 62,
      startVelocity: 28,
      gravity: 1.2,
      decay: 0.94,
      ticks: 180,
      scalar: 0.8,
      shapes: ['circle'],
      // Los colores de la casa: con la paleta que trae la librería, el confeti
      // parecía recortado de otra página.
      colors: ['#4b93e4', '#8fc0f2', '#ffffff', '#12263c'],
      origin: {
        x: (caja.left + caja.width / 2) / window.innerWidth,
        y: (caja.top + caja.height / 2) / window.innerHeight,
      },
      disableForReducedMotion: true,
    });
  }

  function cambiar(nuevo: Ciclo) {
    if (nuevo === ciclo) return;
    setCiclo(nuevo);
    if (nuevo === 'ano') void celebrar();
  }

  return (
    <section id="precios" className="precios">
      <h2>Precios</h2>
      <p className="precios__lead">
        En dólares, con cargo internacional. Cambia de plan cuando quieras.
      </p>

      {/*
        Dos opciones con su nombre escrito, y no un interruptor suelto: en un
        interruptor hay que deducir qué significa apagado, y aquí lo apagado
        también es una opción que se vende.
      */}
      <div
        ref={mando}
        className="precios__ciclo"
        role="radiogroup"
        aria-labelledby={etiqueta}
      >
        <span className="visualmente-oculto" id={etiqueta}>
          Ciclo de pago
        </span>
        <button
          type="button"
          role="radio"
          aria-checked={ciclo === 'mes'}
          className="precios__opcion"
          onClick={() => cambiar('mes')}
        >
          Mensual
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={ciclo === 'ano'}
          className="precios__opcion"
          onClick={() => cambiar('ano')}
        >
          Anual
          <span className="precios__ahorro">{MESES_GRATIS_AL_ANO} meses gratis</span>
        </button>
      </div>

      <div className="precios__rejilla">
        {listedPlans().map((plan) => {
          const destacado = plan.id === 'pro';
          return (
            <article key={plan.id} className={`plan${destacado ? ' plan--destacado' : ''}`}>
              {destacado ? (
                <span className="plan__insignia">
                  <Star size={12} strokeWidth={0} fill="currentColor" aria-hidden />
                  El más elegido
                </span>
              ) : null}

              <h3>{plan.name}</h3>

              <p className="plan__precio">
                {plan.monthlyPriceUsdCents === 0 ? (
                  /*
                    El gratuito se escribe a pelo y sin decimales.
                    "$0.00" obliga a leer dos ceros que no dicen nada, y el
                    número tampoco tiene de dónde a dónde rodar: vale cero en
                    los dos ciclos.
                  */
                  <span>{formatUsd(0)}</span>
                ) : (
                  /*
                    El número rueda de un precio al otro. Es el único sitio de la
                    página donde una animación dice algo que el texto no dice: de
                    cuánto a cuánto acaba de bajar.
                  */
                  <NumberFlow
                    value={precioDe(plan, ciclo)}
                    locales="en-US"
                    format={DOLARES}
                    transformTiming={{ duration: 520, easing: 'ease-out' }}
                    willChange
                  />
                )}
                <span>/mes</span>
              </p>
              <p className="plan__cobro">{comoSeCobra(plan, ciclo === 'ano')}</p>

              <ul className="plan__rasgos">
                {rasgosDe(plan.limits).map((r) => (
                  <li key={r.texto} className={r.incluido ? '' : 'plan__rasgo--no'}>
                    <span className="plan__marca" aria-hidden>
                      {r.incluido ? (
                        <Check size={14} strokeWidth={2.6} />
                      ) : (
                        <Minus size={14} strokeWidth={2.6} />
                      )}
                    </span>
                    {r.texto}
                  </li>
                ))}
              </ul>

              {/*
                El boton lleva a contratar ESTE plan, no a la puerta.
              
                Quien pulsa aqui ya ha elegido. Mandarlo a la pantalla de acceso a
                secas le hace repetir la eleccion al otro lado, y antes acababa en
                "Codigos" sin que nadie volviera a mencionar el plan.
              
                Sin sesion, el panel lo manda a entrar y vuelve aqui solo; con
                sesion, entra directo. El gratuito es la excepcion: ahi no hay nada
                que contratar, solo que empezar.
              */}
              <a
                className={`btn ${destacado ? 'btn--primary' : 'btn--ghost'} plan__cta`}
                href={
                  plan.monthlyPriceUsdCents === 0
                    ? `${ADMIN_URL}/acceso`
                    : `${ADMIN_URL}/facturacion?plan=${plan.id}&ciclo=${ciclo}`
                }
              >
                {plan.monthlyPriceUsdCents === 0 ? 'Empezar gratis' : `Elegir ${plan.name}`}
              </a>
            </article>
          );
        })}
      </div>

      <p className="precios__nota">
        Los planes de pago <strong>no tienen tope de escaneos</strong>: un código
        impreso no deja de funcionar a mitad de mes. ¿Necesitas más de lo que cabe en{' '}
        {PLANS.business.name}? Escríbenos.
      </p>
      <p className="precios__nota precios__nota--menuda">
        El precio anual se cobra de una vez y equivale a pagar {MESES_QUE_SE_PAGAN} meses
        de {MESES_DEL_ANO}.
      </p>
    </section>
  );
}
